import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { timeout, interval } from 'd3-timer'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Center,
} from '@chakra-ui/react';
import { StaticImage } from 'gatsby-plugin-image';
import { navigate } from 'gatsby';

import Container from '../../components/Container';
import Box from '../../components/Box';
import Flex from '../../components/Flex';
import Text from '../../components/Text';
import Button from '../../components/Button';
// import Image from '../../components/Image';
import HighlightNumber from '../../components/HighlightNumber';
import Progress from '../../components/Progress';

import Cards from './Cards';
import Controls from './Controls';

// import hand from './hand.svg'
import { responsive } from '../../components/ThemeProvider/theme';
import useQuestions from './useQuestions';
import JustfontBg from './JustfontBg';
import useAnswerTools from '../useAnswerTools';
import Finished from '../Finished';

let ticker
let ansTime
const secDuration = 40
const sec = secDuration * 1000
const GamePage = ({ location, data }) => {
  const path = /[^/]+/g.exec(location.pathname)[0]
  const { questions, results } = useQuestions({ ...data, path })
  const [tutorial, setTutorial] = useState(true)
  const [no, setNo] = useState(1)
  const [finish, setFinish] = useState()
  const [canAnswer, setCanAnswer] = useState(true)
  const [answers, setAnswers] = useState([])
  const [time, setTime] = useState(data.sec || sec)
  const quizId = /^\/([^/]+)/.exec(location.pathname)
  const answerTools = useAnswerTools(quizId && quizId[1])

  const timeUp = () => {
    if (ticker) {
      ticker.stop()
    }
    setFinish(1)
  }
  const onAnswer = useCallback((i) => {
    setCanAnswer(false)
    setAnswers(ans => {
      const newAns = [...ans]
      newAns[no - 1] = {
        ans: i,
        duration: new Date() - ansTime,
        optionId: questions[no - 1]?.id,
      }
      return newAns
    })
    timeout(() => {
      if (time > 0 && no < questions.length) {
        ansTime = new Date()
        setNo(no + 1)
      } else {
        timeUp()
      }
      setCanAnswer(true)
    }, 100)
  }, [no, questions])
  const tick = useCallback((elapsed) => {
    setTime(() => {
      if ((data.sec || sec) > elapsed) {
        return (data.sec || sec) - elapsed
      } else {
        timeUp()
        return 0
      }
    })
  }, [])
  useEffect(() => {
    if (!tutorial && questions.length) {
      ansTime = new Date()
      ticker = interval(tick)
    }
  }, [tutorial, questions])
  useEffect(() => {
    if (finish) {
      const go = () => navigate(location.pathname.replace('play', 'result'), { state: { answers, questions, results } })
      answerTools.updateAnswer(answers).then(go)
    }
  }, [finish])
  return (
    <Box height={`full`} bg={`${data.bg}.400`} overflowX="hidden" overflowY="auto" mt="0">
      <Container px="2em" height="100%" maxWidth={1200}>
        <Box.Relative height="100%">
          <Progress color={`${data.bg}.400`} value={time} total={data.sec || sec} position="relative" zIndex={1} />
          {finish ? (
            <Center height="50vh">
              <Finished title="挑戰結束！" />
            </Center>
          ) : (
            <>
              <Box.Absolute top="0" left="0" right="0" bottom="0">
                <JustfontBg height="100%" position="relative">
                  {questions.length ? (
                    <Cards
                      no={no}
                      answers={answers}
                      onAnswer={onAnswer}
                      disabled={!canAnswer}
                      questions={questions}
                      borderColor={data.cardBorder}
                    />
                  ) : null}
                </JustfontBg>
              </Box.Absolute>
              <Controls path={path} onAnswer={onAnswer} disabled={!canAnswer} />
            </>
          )}
        </Box.Relative>
      </Container>
      {useMemo(() => (
        <Modal isOpen={tutorial} closeOnOverlayClick={false} isCentered>
          <ModalOverlay />
          <ModalContent
            maxWidth={responsive('90vw', '30em')}
            rounded="2em"
            overflow="hidden"
            border="0.25em solid black"
            textAlign="center"
          >
            <ModalHeader
              bg="gray.200"
              textAlign="center"
              lineHeight="2"
              fontSize={responsive('1.75em', '1.75em')}
              fontWeight="500"
              letterSpacing="0.125em"
            >遊戲操作教學</ModalHeader>
            <ModalBody bg="gray.200" px="4em">
              <Box px="25%">
                <StaticImage alt="手指" src="hand.svg" />
              </Box>
              <Text
                lineHeight="2"
                fontWeight="900"
                fontSize={responsive('1.375em', '1.75em')}
                letterSpacing="0.125em"
              >左右拖曳來回答</Text>
            </ModalBody>
            <ModalFooter display="block" p={responsive('2em', '1em')}>
              <Flex justifyContent="center" fontSize={responsive('1.5em', '1.5em')}>
                <HighlightNumber numberProps={{ fontSize: '2em', color: data.buttonColor ? `${data.buttonColor}.300` : 'yellow.500' }} number={data.sec / 1000 || secDuration}>秒快問快答</HighlightNumber>
              </Flex>
              <Button.Pink
                isLoading={answerTools.loading}
                fontSize={responsive('1.75em', '2em')}
                bg={data.buttonColor && `${data.buttonColor}.400`}
                _hover={{
                  bg: data.buttonColor && `${data.buttonColor}.500`
                }}
                color={data.color}
                width="full"
                onClick={() => answerTools.init().then(() => setTutorial(false))}
              >開始挑戰</Button.Pink>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ), [tutorial, answerTools])}
    </Box>
  )
}

export default GamePage
